// components/Navbar/MobileDrawer.jsx
import { Link } from 'react-router-dom';
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  IconButton,
  alpha,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HomeIcon from '@mui/icons-material/Home';
import FactoryIcon from '@mui/icons-material/Factory';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import DiamondIcon from '@mui/icons-material/Diamond';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import InsightsIcon from '@mui/icons-material/Insights';

const mobileNavItems = [
  { path: '/', label: 'Home', icon: <HomeIcon /> },
  { path: '/mining', label: 'Mining', icon: <FactoryIcon /> },
  { path: '/coin-minting', label: 'Coin Minting', icon: <MonetizationOnIcon /> },
  { path: '/metal-processing', label: 'Processing', icon: <PrecisionManufacturingIcon /> },
  { path: '/distribution', label: 'Distribution', icon: <LocalShippingIcon /> },
  { path: '/market-data', label: 'Market Data', icon: <ShowChartIcon /> },
  { path: '/contact', label: 'Contact', icon: <ContactMailIcon /> },
];

const privateOfficeItems = [
  { path: '/private/dashboard', label: 'Executive Dashboard', icon: <DashboardIcon /> },
  { path: '/private/portfolio', label: 'Portfolio Management', icon: <AccountBalanceWalletIcon /> },
  { path: '/private/analysis', label: 'Market Analysis', icon: <InsightsIcon /> },
];

const MobileDrawer = ({ open, onClose }) => {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: '85%',
          maxWidth: 320,
          bgcolor: '#000066',
          backgroundImage: 'linear-gradient(135deg, #000066 0%, #0a0a3a 100%)',
          borderLeft: '1px solid rgba(212, 175, 55, 0.2)',
          boxShadow: '-10px 0 30px rgba(0, 0, 0, 0.3)',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid rgba(212, 175, 55, 0.15)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DiamondIcon sx={{ color: '#D4AF37', fontSize: 24 }} />
          <Typography
            sx={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #D4AF37 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 800,
              fontSize: '1.2rem',
            }}
          >
            Menu
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: '#D4AF37',
            '&:hover': {
              backgroundColor: alpha('#D4AF37', 0.15),
              transform: 'rotate(90deg)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Navigation Links */}
      <List sx={{ p: 2 }}>
        {mobileNavItems.map((item) => (
          <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              component={Link}
              to={item.path}
              onClick={onClose}
              sx={{
                borderRadius: 2,
                py: 1.2,
                px: 2,
                '&:hover': {
                  backgroundColor: alpha('#D4AF37', 0.1),
                  '& .MuiListItemIcon-root': {
                    color: '#D4AF37',
                    transform: 'translateX(4px)',
                  },
                  '& .MuiListItemText-primary': {
                    color: '#D4AF37',
                  },
                },
                transition: 'all 0.2s ease',
              }}
            >
              <ListItemIcon sx={{ color: alpha('#FFFFFF', 0.7), minWidth: 40, transition: 'all 0.2s ease' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.label} 
                primaryTypographyProps={{ 
                  sx: { 
                    color: alpha('#FFFFFF', 0.85), 
                    fontWeight: 500,
                    fontSize: '0.95rem',
                    transition: 'color 0.2s ease',
                  } 
                }} 
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider sx={{ mx: 2, borderColor: alpha('#D4AF37', 0.15) }} />

      {/* Private Office Section */}
      <Typography
        sx={{
          px: 3,
          py: 1.5,
          color: '#D4AF37',
          fontWeight: 700,
          fontSize: '0.7rem',
          letterSpacing: 1.5,
          textTransform: 'uppercase',
        }}
      >
        Private Office
      </Typography>
      
      <List sx={{ p: 2, pt: 0 }}>
        {privateOfficeItems.map((item) => (
          <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              component={Link}
              to={item.path}
              onClick={onClose}
              sx={{
                borderRadius: 2,
                py: 1.2,
                px: 2,
                '&:hover': {
                  backgroundColor: alpha('#D4AF37', 0.1),
                  '& .MuiListItemIcon-root': {
                    color: '#D4AF37',
                  },
                  '& .MuiListItemText-primary': {
                    color: '#D4AF37',
                  },
                },
                transition: 'all 0.2s ease',
              }}
            >
              <ListItemIcon sx={{ color: alpha('#FFFFFF', 0.7), minWidth: 40, transition: 'all 0.2s ease' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.label} 
                primaryTypographyProps={{ 
                  sx: { 
                    color: alpha('#FFFFFF', 0.85), 
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    transition: 'color 0.2s ease',
                  } 
                }} 
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider sx={{ mx: 2, borderColor: alpha('#D4AF37', 0.15) }} />

      {/* Premium CTA */}
      <Box sx={{ p: 3, mt: 'auto' }}>
        <Button
          variant="contained"
          fullWidth
          component={Link}
          to="/contact"
          onClick={onClose}
          sx={{
            background: 'linear-gradient(135deg, #D4AF37 0%, #B8960C 100%)',
            color: '#000066',
            fontWeight: 700,
            py: 1.2,
            borderRadius: 2,
            '&:hover': {
              background: 'linear-gradient(135deg, #E5C158 0%, #D4AF37 100%)',
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 20px rgba(212, 175, 55, 0.3)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          <DiamondIcon sx={{ fontSize: 16, mr: 1 }} />
          Contact Us
        </Button>
      </Box>
    </Drawer>
  );
};

export default MobileDrawer;